import {
  ADDRESS_FIELDS_TO_SEND,
  APPLICANT_FIELDS_TO_SEND,
  CHILD_FIELDS_TO_SEND,
  DOCUMENT_FIELDS_TO_SEND,
  ORGANIZATION_FIELDS_TO_SEND,
  PERSON_FIELDS_TO_SEND,
  SECOND_PARENT_FIELDS_TO_SEND,
} from '../constants/fieldNamesToSend';
import { getTextValue, getValue } from '../constants/getFieldValues';
import {
  formatDateToSubmit,
  isUnder23YearsOld,
  shouldBeGettingEducation,
  shouldHaveIdentification,
} from './dateHelpers';
import {
  CHANGE_NAME_DOC_ISSUER,
  CHANGED_FIO_FIELD_NAME,
  CHILD_FIELD_NAME,
  IS_CHANGED_PATRONYMIC_ABSENT,
  PERSON_FIELD_NAME,
  SECOND_PARENT_FIELD_NAME,
} from '../constants/fieldNames';
import {
  BORN_IN,
  BORN_IN_MARRIAGE,
  CHANGE_NAME,
  CHILD_ADDRESS,
  CONFIRMING_DOCUMENTS,
} from '../constants/child';
import {
  DEAD_PARENT,
  SECOND_PARENT_STATUS,
} from '../constants/secondParentStatus';
import { DOCUMENT_TYPE, DocumentFields } from '../constants/document';
import { notEmptyField } from '../validators/common';
import { AddressFields } from '../../types/address';
import { isNewMoscow } from '../selectors/addressUnmodeled';
import {
  AddressFieldsForSubmit,
  ChildAddressForSubmit,
  SpouseAddressForSubmit,
} from '../models/submit';
import { Field } from '../declarations/widget-ums';
import { Organization } from './initialState';

const personalInfoFields = [
  ...Object.keys(PERSON_FIELDS_TO_SEND),
  CHANGE_NAME_DOC_ISSUER,
];
const applicantFields = Object.keys(APPLICANT_FIELDS_TO_SEND);
const addressFields = Object.keys(ADDRESS_FIELDS_TO_SEND);
const childFields = [
  ...Object.keys(CHILD_FIELDS_TO_SEND),
  CHILD_FIELD_NAME.bornInNotCurrentMarriage,
];
const documentFields = Object.keys(DOCUMENT_FIELDS_TO_SEND);
const secondParentFields = [
  ...Object.keys(SECOND_PARENT_FIELDS_TO_SEND),
  SECOND_PARENT_FIELD_NAME.status,
  SECOND_PARENT_FIELD_NAME.proofOfDeath,
];

function isPersonalInfoField(key) {
  return personalInfoFields.includes(key);
}

function isApplicantFields(key) {
  return applicantFields.includes(key);
}

function isAddressFields(key) {
  return addressFields.includes(key);
}

function isChildFields(key) {
  return childFields.includes(key);
}

function isDocumentFields(key) {
  return documentFields.includes(key);
}

function isSecondParentFields(key) {
  return secondParentFields.includes(key);
}

const valueToSubmit = (value) => (!notEmptyField(value) ? value : null);

export const addStreetForZelenograd = (person) => {
  if (!person) return person;

  if (
    person['address'] &&
    person['address'][ADDRESS_FIELDS_TO_SEND.area] &&
    person['address'][ADDRESS_FIELDS_TO_SEND.area] === 'Зеленоградский АО' &&
    !person['address'][ADDRESS_FIELDS_TO_SEND.street]
  ) {
    person['address'][ADDRESS_FIELDS_TO_SEND.street] = 'Зеленоград';
  }

  return person;
};

export const addressToSubmit: (
  addressFields: AddressFields
) => AddressFieldsForSubmit = (addressFields: AddressFields) => {
  const isInNewMoscow = isNewMoscow(
    { person: { address: addressFields } },
    { model: 'person' }
  );
  return Object.keys(addressFields)
    .filter(isAddressFields)
    .reduce(
      (next, key) => {
        if (key === ADDRESS_FIELDS_TO_SEND.area) {
          return {
            ...next,
            [ADDRESS_FIELDS_TO_SEND[key]]: valueToSubmit(
              getTextValue(addressFields[key])
            ),
          };
        }

        const districtFields = ['district', 'settlement'];
        if (districtFields.includes(key)) {
          return {
            ...next,
            [ADDRESS_FIELDS_TO_SEND.district]: valueToSubmit(
              districtFields.reduce(
                (district, settlement) =>
                  getTextValue(addressFields[district]) ||
                  getTextValue(addressFields[settlement])
              )
            ),
          };
        }

        if (
          ADDRESS_FIELDS_TO_SEND.isManualInput === ADDRESS_FIELDS_TO_SEND[key]
        ) {
          return {
            ...next,
            [ADDRESS_FIELDS_TO_SEND[key]]:
              valueToSubmit(addressFields[key]) || isInNewMoscow,
          };
        }

        if (
          [
            ADDRESS_FIELDS_TO_SEND.bti_building_code,
            ADDRESS_FIELDS_TO_SEND.isServeByEirc,
            ADDRESS_FIELDS_TO_SEND.fiasId,
          ].includes(ADDRESS_FIELDS_TO_SEND[key])
        ) {
          return {
            ...next,
            [ADDRESS_FIELDS_TO_SEND[key]]: valueToSubmit(addressFields[key]),
          };
        }
        return {
          ...next,
          [ADDRESS_FIELDS_TO_SEND[key]]: valueToSubmit(
            getValue<string>(addressFields[key])
          ),
        };
      },
      {
        omk_district_code: addressFields.omk_district_code,
        omk_area_code: addressFields.omk_area_code,
        omk_street_code: addressFields.omk_street_code,
        region: 'Москва',
      }
    ) as AddressFieldsForSubmit;
};
const patronymicToSend = (fieldsObject, name, isEmptyName) => {
  const isPatronymicEmpty = getValue(fieldsObject[isEmptyName]);
  return {
    [PERSON_FIELDS_TO_SEND.isPatronymicAbsent]: isPatronymicEmpty,
    [PERSON_FIELDS_TO_SEND.patronymic]: isPatronymicEmpty
      ? null
      : getValue(fieldsObject[name]),
  };
};

export const personToSubmit = (personFields) => {
  const patronymic = patronymicToSend(
    personFields,
    PERSON_FIELD_NAME.patronymic,
    PERSON_FIELD_NAME.isPatronymicAbsent
  );
  return Object.keys(personFields)
    .filter(isPersonalInfoField)
    .reduce(
      (next, key) => {
        if (typeof personFields[key] === 'object' && personFields[key].error)
          return next;
        if (
          [
            PERSON_FIELDS_TO_SEND.isPatronymicAbsent,
            PERSON_FIELDS_TO_SEND.patronymic,
            PERSON_FIELDS_TO_SEND.changeNameDocIssuer,
          ].includes(PERSON_FIELDS_TO_SEND[key])
        )
          return next;

        if (PERSON_FIELDS_TO_SEND[key] === PERSON_FIELDS_TO_SEND.birthdate) {
          return {
            ...next,
            [PERSON_FIELDS_TO_SEND[key]]: formatDateToSubmit(
              getValue(personFields[key])
            ),
          };
        }

        if (PERSON_FIELDS_TO_SEND[key] === PERSON_FIELDS_TO_SEND.changeName) {
          const value = getValue(personFields[key]);
          if (!value)
            return {
              ...next,
              [PERSON_FIELDS_TO_SEND.isNameChanged]: null,
            };
          if (value === CHANGE_NAME.match) {
            return {
              ...next,
              [PERSON_FIELDS_TO_SEND.changeName]: null,
              [PERSON_FIELDS_TO_SEND.isNameChanged]: false,
            };
          }

          const changedPatronymic = patronymicToSend(
            personFields,
            CHANGED_FIO_FIELD_NAME.changedPatronimic,
            IS_CHANGED_PATRONYMIC_ABSENT
          );

          const newField = {
            [PERSON_FIELDS_TO_SEND.isNameChanged]: true,
            [PERSON_FIELDS_TO_SEND.changeName]: {
              [PERSON_FIELDS_TO_SEND.name]: valueToSubmit(
                getValue(personFields.changedName)
              ),
              [PERSON_FIELDS_TO_SEND.surname]: valueToSubmit(
                getValue(personFields.changedSurname)
              ),
              ...changedPatronymic,
              [PERSON_FIELDS_TO_SEND.changeNameDocIssuer]: getValue(
                personFields.changeNameDocIssuer
              ),
            },
          };
          return { ...next, ...newField };
        }
        if (
          PERSON_FIELDS_TO_SEND[key] ===
          PERSON_FIELDS_TO_SEND.isRegisteredInMoscow
        ) {
          return {
            ...next,
            [PERSON_FIELDS_TO_SEND[key]]: personFields[key],
          };
        }
        return {
          ...next,
          [PERSON_FIELDS_TO_SEND[key]]: getValue(personFields[key]),
        };
      },
      { ...patronymic }
    );
};
export const applicantToSubmit = (obj) => {
  const personData = personToSubmit(obj);
  return Object.keys(obj)
    .filter(isApplicantFields)
    .reduce((next, key) => {
      if (obj[key].error) return next;
      if (key === APPLICANT_FIELDS_TO_SEND.phone) {
        const phone = getValue(obj[key])
          .replace(/(\+7)/, '')
          .replace(/(\D)/gm, '');
        return {
          ...next,
          [APPLICANT_FIELDS_TO_SEND[key]]: phone,
        };
      }
      if (key === APPLICANT_FIELDS_TO_SEND.snils) {
        return {
          ...next,
          [APPLICANT_FIELDS_TO_SEND[key]]: getValue(obj[key]).replace(
            /(\D)/gm,
            ''
          ),
        };
      }
      return { ...next, [APPLICANT_FIELDS_TO_SEND[key]]: getValue(obj[key]) };
    }, personData);
};
export const documentForSubmit: (
  values: DocumentFields,
  birthPlace
) => Record<string, unknown> = (
  values: DocumentFields,
  birthPlace: Field<string>
) => {
  return Object.keys(values)
    .filter(isDocumentFields)
    .reduce(
      (next, key) => {
        if (values[key].error) return next;

        if (DOCUMENT_FIELDS_TO_SEND[key] === DOCUMENT_FIELDS_TO_SEND.type) {
          return {
            ...next,
            [DOCUMENT_FIELDS_TO_SEND[key]]:
              DOCUMENT_TYPE[+getValue(values[key])],
          };
        }
        if (
          DOCUMENT_FIELDS_TO_SEND[key] === DOCUMENT_FIELDS_TO_SEND.issueDate
        ) {
          return {
            ...next,
            [DOCUMENT_FIELDS_TO_SEND[key]]: formatDateToSubmit(
              getValue(values[key])
            ),
          };
        }
        return {
          ...next,
          [DOCUMENT_FIELDS_TO_SEND[key]]: valueToSubmit(getValue(values[key])),
        };
      },
      {
        [DOCUMENT_FIELDS_TO_SEND.birthPlace]: valueToSubmit(
          getValue(birthPlace)
        ),
      }
    );
};

const organizationToSubmit: (
  fields: Organization
) => Record<string, unknown> | null = (fields: Organization) => {
  if (!fields || !getValue(fields.institutionName)) return null;

  return Object.keys(fields).reduce((next, key) => {
    if (!ORGANIZATION_FIELDS_TO_SEND[key]) return next;
    if (
      ORGANIZATION_FIELDS_TO_SEND[key] ===
      ORGANIZATION_FIELDS_TO_SEND.isFoundInCatalog
    ) {
      return {
        ...next,
        ...{ [ORGANIZATION_FIELDS_TO_SEND[key]]: fields[key] },
      };
    }
    return {
      ...next,
      ...{ [ORGANIZATION_FIELDS_TO_SEND[key]]: getValue(fields[key]) },
    };
  }, {});
};

export const getOrganizations = ({
  birthdate,
  education,
  polyclinic,
  isStudying,
  confirmingDocs,
}) => {
  if (!isUnder23YearsOld(birthdate.value)) return null;
  const shoulBeStudying = shouldBeGettingEducation(birthdate.value);

  const confirmingDocsValue = getValue(confirmingDocs);

  const organisationsData = {
    ...(isStudying ? { education: organizationToSubmit(education) } : {}),
    polyclinic: organizationToSubmit(polyclinic),
    is_studying: isStudying,
  };

  if (confirmingDocsValue) {
    const isIndirectly =
      confirmingDocsValue === CONFIRMING_DOCUMENTS.indirectly;
    const confirmingDocsToSend = {
      [CHILD_FIELDS_TO_SEND.confirmingDocs]: confirmingDocsValue,
    };
    if (!isIndirectly && !shoulBeStudying) return confirmingDocsToSend;
    return { ...organisationsData, ...confirmingDocsToSend };
  }

  if (shoulBeStudying) {
    return organisationsData;
  }

  return null;
};

export const childForSubmit = (child) => {
  const {
    birthdate,
    name,
    surname,
    patronymic,
    isPatronymicAbsent,
    sex,
    changeName,
    changedName,
    changedSurname,
    changedPatronimic,
    isStudying,
    education,
    polyclinic,
    confirmingDocs,
    ...restFields
  } = child;

  return Object.keys(restFields)
    .filter(isChildFields)
    .reduce(
      (next, key) => {
        if (restFields[key].error) return next;
        if (
          CHILD_FIELDS_TO_SEND[key] ===
          CHILD_FIELDS_TO_SEND.bornInCurrentMarriage
        ) {
          const value = getValue(restFields[key]);
          return {
            ...next,
            [CHILD_FIELDS_TO_SEND[key]]: value
              ? value === BORN_IN.currentMarriage
              : null,
          };
        }
        if (key === CHILD_FIELD_NAME.bornInNotCurrentMarriage) {
          const value = getValue(restFields[key]);
          if (!value) return next;

          const isApplicantChild = value === BORN_IN_MARRIAGE.ofApplicant;
          const fieldName = isApplicantChild
            ? CHILD_FIELDS_TO_SEND.isApplicantChild
            : CHILD_FIELDS_TO_SEND.isSpouseChild;
          return { ...next, [fieldName]: true };
        }
        return {
          ...next,
          [CHILD_FIELDS_TO_SEND[key]]: getValue(restFields[key]),
        };
      },
      {
        ...personToSubmit(child),
        ...getOrganizations({
          birthdate,
          education,
          polyclinic,
          isStudying,
          confirmingDocs,
        }),
      }
    );
};

export const checkChildIsAdult = (birthdate, childPassport): boolean => {
  return (
    shouldHaveIdentification(getValue(birthdate)) ||
    ['number', 'issued_at', 'issued_by', 'division_code'].every((key) => {
      return !!childPassport[key];
    })
  );
};

export const childAddressToSubmit = (
  addressMatchWithApplicant: Field<CHILD_ADDRESS | null>,
  childAddress: AddressFields,
  applicantAddress: AddressFieldsForSubmit,
  spouseAddress: SpouseAddressForSubmit,
  isRegisteredInMoscow
): ChildAddressForSubmit => {
  const value = getValue<CHILD_ADDRESS>(
    addressMatchWithApplicant as Field<CHILD_ADDRESS>
  );
  if (!value) return null;
  switch (CHILD_ADDRESS[value]) {
    case CHILD_ADDRESS.matchWithApplicant:
      return {
        ...applicantAddress,
        [ADDRESS_FIELDS_TO_SEND.addressMatchWithApplicant]:
          CHILD_ADDRESS.matchWithApplicant,
      };
    case CHILD_ADDRESS.matchWithSpouse:
      return spouseAddress
        ? {
            ...spouseAddress,
            [ADDRESS_FIELDS_TO_SEND.addressMatchWithApplicant]:
              CHILD_ADDRESS.matchWithSpouse,
          }
        : {
            [ADDRESS_FIELDS_TO_SEND.addressMatchWithApplicant]:
              CHILD_ADDRESS.matchWithSpouse,
          };
    default:
      return isRegisteredInMoscow
        ? {
            ...addressToSubmit(childAddress),
            [ADDRESS_FIELDS_TO_SEND.addressMatchWithApplicant]: valueToSubmit(
              value
            ),
          }
        : {
            [ADDRESS_FIELDS_TO_SEND.addressMatchWithApplicant]: valueToSubmit(
              value
            ),
          };
  }
};

const checkSecondParentStatus = (status) => {
  switch (status) {
    case SECOND_PARENT_STATUS.single:
      return {
        is_single_parent: true,
      };
    case SECOND_PARENT_STATUS.paternity:
      return {
        is_paternity_established: true,
      };
    case SECOND_PARENT_STATUS.widowed:
      return {
        is_second_parent_died: true,
      };
    case SECOND_PARENT_STATUS.divorced:
      return {
        is_marriage_terminated: true,
      };
    default:
      return null;
  }
};
const checkSecondParentProofOfDeath = (value) => {
  switch (value) {
    case DEAD_PARENT.hasDeathCert:
      return {
        have_death_certificate: true,
      };
    case DEAD_PARENT.courtDecision:
      return {
        have_death_court_decision: true,
      };
    default:
      return null;
  }
};

export const secondParentToSubmit = (secondParentFields) => {
  const { status, ...restFields } = secondParentFields;
  const statusToSend = checkSecondParentStatus(getValue(status));
  if (!statusToSend) return null;
  if (statusToSend.is_single_parent) return { second_parent: statusToSend };
  const patronymic = patronymicToSend(
    restFields,
    PERSON_FIELD_NAME.patronymic,
    PERSON_FIELD_NAME.isPatronymicAbsent
  );

  const secondParentResult = Object.keys(restFields)
    .filter(isSecondParentFields)
    .reduce(
      (next, key) => {
        if (secondParentFields[key].error) return next;
        if (
          !getValue(secondParentFields[key]) ||
          [
            PERSON_FIELD_NAME.patronymic,
            PERSON_FIELD_NAME.isPatronymicAbsent,
          ].includes(key)
        )
          return next;

        const dateFields = [
          SECOND_PARENT_FIELDS_TO_SEND.birthdate,
          SECOND_PARENT_FIELDS_TO_SEND.deathdate,
          SECOND_PARENT_FIELDS_TO_SEND.paternityRegisterDate,
          SECOND_PARENT_FIELDS_TO_SEND.dateOfMarriage,
          SECOND_PARENT_FIELDS_TO_SEND.dateOfDivorce,
        ];

        if (dateFields.includes(SECOND_PARENT_FIELDS_TO_SEND[key])) {
          return {
            ...next,
            [SECOND_PARENT_FIELDS_TO_SEND[key]]: formatDateToSubmit(
              getValue(secondParentFields[key])
            ),
          };
        }

        if (key === SECOND_PARENT_FIELD_NAME.proofOfDeath) {
          return {
            ...next,
            ...checkSecondParentProofOfDeath(getValue(secondParentFields[key])),
          };
        }
        return {
          ...next,
          [SECOND_PARENT_FIELDS_TO_SEND[key]]: getValue(
            secondParentFields[key]
          ),
        };
      },
      { ...statusToSend, ...patronymic }
    );

  return { second_parent: secondParentResult };
};
